import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PushNotificationProvider from './components/PushNotificationManager';
import TrainingPendingLoginAlert from './components/TrainingPendingLoginAlert';
import MySalaryLeave from './pages/MySalaryLeave';
import AttendancePage from './pages/AttendancePage';
import FeaturePermissionsPage from './pages/FeaturePermissionsPage';
import NotificationsPage from './pages/NotificationsPage';
import MyNotificationsPage from './pages/MyNotificationsPage';
import { userMayAccessWorkspaceCard } from './utils/workspacePerms';
import './App.css';
import './theme/theme.css';
import { ThemeProvider } from './contexts/ThemeContext';

class RemoteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static isChunkLoadError(error) {
    const message = String(
      error?.message ||
      error?.toString?.() ||
      ''
    );
    return /ChunkLoadError|Loading chunk [\w-]+ failed|Loading chunk .* failed|Failed to fetch dynamically imported module/i.test(message);
  }
  static tryAutoRecoverChunkError() {
    if (typeof window === 'undefined') return false;
    try {
      const key = '__mrt_chunk_reload_once__';
      if (window.sessionStorage.getItem(key) === '1') return false;
      window.sessionStorage.setItem(key, '1');
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('__chunk_reload', String(Date.now()));
      setTimeout(() => {
        try {
          window.location.replace(nextUrl.toString());
        } catch (e) {
          window.location.reload();
        }
      }, 80);
      return true;
    } catch (e) {
      return false;
    }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Remote Error caught:", error, errorInfo);
    this.setState({ errorInfo });
    if (RemoteErrorBoundary.isChunkLoadError(error)) {
      RemoteErrorBoundary.tryAutoRecoverChunkError();
    }
  }
  render() {
    if (this.state.hasError) {
      const isChunkError = RemoteErrorBoundary.isChunkLoadError(this.state.error);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 16, padding: 20 }}>
          <div style={{ fontSize: 16, color: '#e74c3c', fontWeight: 800 }}>⚠️ 화면을 불러오지 못했습니다.</div>
          {isChunkError ? (
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 700, textAlign: 'center' }}>
              청크 캐시 불일치가 감지되어 자동 새로고침을 시도했습니다.
            </div>
          ) : null}
          <div style={{ background: '#f8d7da', padding: 10, borderRadius: 5, color: '#721c24', fontSize: 12, width: '100%', maxWidth: '90vw', overflowX: 'auto' }}>
            <strong>{this.state.error && this.state.error.toString()}</strong>
            <br /><br />
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 10 }}>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </pre>
          </div>
          <button
            style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}
            onClick={() => {
              try {
                const nextUrl = new URL(window.location.href);
                nextUrl.searchParams.set('__chunk_reload', String(Date.now()));
                window.location.replace(nextUrl.toString());
              } catch (e) {
                window.location.reload();
              }
            }}
          >
            화면 강제 새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PAGE_TITLES = {
  '/': '대시보드',
  '/login': '로그인',
  '/schedule': '일정',
  '/erp': 'ERP',
  '/my-salary-leave': '내 급여 및 연차',
  '/attendance': '출퇴근 체크',
  '/vehicle-operation': '차량운행',
  '/internal-training': '사내교육 이수',
  '/receipt': '영수증 등록',
  '/feature-permissions': '기능 권한 관리',
  '/notifications': '공지 푸시 발송',
  '/my-notifications': '내 알림 내역',
};

const PageTitle = () => {
  const location = useLocation();
  useEffect(() => {
    const menu = new URLSearchParams(location.search).get('menu');
    const erpMenuTitle = {
      receipt: '영수증 등록',
      vehicle_operation: '차량운행',
      internal_training: '사내교육 이수',
    }[menu];
    const name = (location.pathname === '/erp' && erpMenuTitle)
      ? erpMenuTitle
      : (PAGE_TITLES[location.pathname] || '');
    document.title = name ? `${name} | MRT Workspace` : 'MRT Workspace';
  }, [location.pathname, location.search]);
  return null;
};

// Micro-Frontend (CS) - loaded from remoteEntry.js via Module Federation
const RemoteSchedule = React.lazy(() => import('csApp/ScheduleMfe'));
const RemoteQrReceive = React.lazy(() => import('csApp/QrReceiveMfe'));
// Micro-Frontend (ERP)
const RemoteErp = React.lazy(() => import('erpApp/ErpMfe'));

const ProtectedRoute = ({ children, requireApproved = true }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const from = `${location.pathname || ''}${location.search || ''}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  const email = String(user?.email || '').trim().toLowerCase();
  const isSuper = email === 'mrt@mrt.co.kr';
  const approvalStatus = String(user?.approval_status || (user?.approved === false ? 'pending' : 'approved')).trim();
  const isApproved = isSuper || approvalStatus === 'approved';

  if (requireApproved && !isApproved) {
    // 승인 전에는 대시보드만 허용(그룹관리 모달에서 가입요청/상태 확인)
    return <Navigate to="/" replace state={{ from: location.pathname, blocked: true }} />;
  }

  return children;
};

/** admin_user.workspace_perms — 직접 URL 접근 시에도 메뉴 권한 적용
 *  카드레지스트리에 bypassWorkspacePerms: true 인 경우, ERP permissions으로 대체 판단
 */
const WorkspacePermRoute = ({ cardId, children, bypassCheck }) => {
  const { user } = useAuth();
  const location = useLocation();
  const email = String(user?.email || '').trim().toLowerCase();
  const isSuper = email === 'mrt@mrt.co.kr';

  // bypassCheck: ERP permissions 기반으로만 판단 (workspace_perms 무시)
  if (bypassCheck) {
    const perms = user?.permissions || [];
    const hasErpPerm = perms.some(p => ['read', 'write', 'admin'].includes(p));
    if (!isSuper && !hasErpPerm) {
      return <Navigate to="/" replace state={{ from: location.pathname, blockedWorkspacePerm: true }} />;
    }
    return children;
  }

  if (!userMayAccessWorkspaceCard(user, cardId, isSuper)) {
    return <Navigate to="/" replace state={{ from: location.pathname, blockedWorkspacePerm: true }} />;
  }
  return children;
};

/** 영수증·차량운행·사내교육 딥링크는 ERP workspace_perms 없이 ERP 읽기 권한만으로 허용 */
const ERP_DEEPLINK_MENUS = new Set(['receipt', 'vehicle_operation', 'internal_training']);

const ErpWorkspaceRoute = ({ children }) => {
  const location = useLocation();
  const menu = String(new URLSearchParams(location.search).get('menu') || '').trim();
  const deepLink = ERP_DEEPLINK_MENUS.has(menu);
  return (
    <WorkspacePermRoute cardId="erp" bypassCheck={deepLink}>
      {children}
    </WorkspacePermRoute>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading, login } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <PushNotificationProvider>
      <PageTitle />
      {isAuthenticated ? <TrainingPendingLoginAlert /> : null}
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login onLoginSuccess={login} />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute requireApproved={false}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="schedule">
                <RemoteErrorBoundary>
                  <React.Suspense
                    fallback={
                      <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>일정 화면 로딩 중...</p>
                      </div>
                    }
                  >
                    <RemoteSchedule />
                  </React.Suspense>
                </RemoteErrorBoundary>
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/qr-receive"
          element={
            <ProtectedRoute>
              <RemoteErrorBoundary>
                <React.Suspense
                  fallback={
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <p>QR 장비 수령 화면 로딩 중...</p>
                    </div>
                  }
                >
                  <RemoteQrReceive />
                </React.Suspense>
              </RemoteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/erp"
          element={
            <ProtectedRoute>
              <ErpWorkspaceRoute>
                <RemoteErrorBoundary>
                  <React.Suspense
                    fallback={
                      <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>ERP 화면 로딩 중...</p>
                      </div>
                    }
                  >
                    <RemoteErp />
                  </React.Suspense>
                </RemoteErrorBoundary>
              </ErpWorkspaceRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-salary-leave"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="my-salary-leave">
                <MySalaryLeave />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="attendance" bypassCheck>
                <AttendancePage />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicle-operation"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="vehicle-operation" bypassCheck>
                <Navigate to="/erp?menu=vehicle_operation" replace />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/internal-training"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="internal-training">
                <Navigate to="/erp?menu=internal_training" replace />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/receipt"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="receipt" bypassCheck>
                <Navigate to="/erp?menu=receipt" replace />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feature-permissions"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="feature-permissions">
                <FeaturePermissionsPage />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <WorkspacePermRoute cardId="notifications">
                <NotificationsPage />
              </WorkspacePermRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-notifications"
          element={
            <ProtectedRoute>
              <MyNotificationsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </PushNotificationProvider>
  );
};

function App() {
  const runtimeClientId =
    (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_GOOGLE_CLIENT_ID) ||
    (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__.REACT_APP_GOOGLE_CLIENT_ID) ||
    process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const clientId = String(runtimeClientId || '').trim();

  // ✅ 정상 동작: Client ID가 없으면 로그인 불가하므로 설정 안내를 표시
  if (!clientId || clientId === 'your-google-client-id-here.apps.googleusercontent.com') {
    return (
      <div className="error-container">
        <h2>⚠️ 설정 오류</h2>
        <p>Google OAuth Client ID가 설정되지 않았습니다.</p>
        <ol>
          <li>
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Cloud Console
            </a>
            에서 OAuth 2.0 클라이언트 ID를 생성하세요.
          </li>
          <li>.env 파일을 생성하고 REACT_APP_GOOGLE_CLIENT_ID를 설정하세요.</li>
          <li>개발 서버를 재시작하세요.</li>
        </ol>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
